/**
 * API publique du domaine "transferts" + helpers de prix.
 *
 * Point d'entrée unique : importer depuis "@/data/transfers".
 *
 * Règle DRY essentielle : les prix ne sont PAS stockés ici. Ils sont lus à la
 * volée dans `VEHICLES[].fixedTransfers` (app/config/infos.ts) via la clé
 * `getRouteKey(fromId, toId)`. Modifier un prix = modifier infos.ts, un seul
 * endroit, et toutes les pages transferts se mettent à jour automatiquement.
 *
 * Dépendances : places.ts, routes.ts, app/config/infos.ts.
 */

import { VEHICLES, getRouteKey, type VehicleId } from "@/config/infos";
import type { Locale } from "@/config/i18n";
import { getTransferPlace, placeShortName, type TransferPlace } from "./places";
import {
  TRANSFER_ROUTES,
  type TransferRoute,
  type TransferRouteContent,
} from "./routes";
import { TRANSFER_ROUTES_EN } from "./routesEn";
import { TRANSFER_ROUTES_AR } from "./routesAr";
import { buildTransferFaq, buildTransferSeo } from "./generatedContent";

export type {
  TransferRoute,
  TransferFaqItem,
  TransferRouteContent,
} from "./routes";
export type { TransferPlace } from "./places";
export { TRANSFER_ROUTES } from "./routes";
export { TRANSFER_PLACES } from "./places";

/**
 * Mapping slug FR ⇄ EN, dérivé (pas de champ à maintenir) : seul le segment
 * "aeroport-nice" change en "nice-airport". Tous les autres slugs sont identiques.
 */
export const toEnSlug = (slug: string): string =>
  slug.replace("aeroport-nice", "nice-airport");
export const fromEnSlug = (enSlug: string): string =>
  enSlug.replace("nice-airport", "aeroport-nice");

/** Slug EN d'un trajet (pour URL/canonical/hreflang). */
export const getRouteEnSlug = (route: TransferRoute): string =>
  toEnSlug(route.slug);

/** Récupère un trajet par slug EN (ou undefined). */
export const getTransferRouteByEnSlug = (
  enSlug: string,
): TransferRoute | undefined => getTransferRoute(fromEnSlug(enSlug));

/** Slugs EN (generateStaticParams + sitemap EN). */
export const transferRouteEnSlugs = (): string[] =>
  TRANSFER_ROUTES.map((route) => toEnSlug(route.slug));

/**
 * Contenu localisé d'un trajet : FR depuis routes.ts, EN depuis routesEn.ts,
 * AR depuis routesAr.ts (fallback FR si une traduction manque — jamais de page
 * vide).
 */
export const localizedRouteContent = (
  route: TransferRoute,
  locale: Locale,
): TransferRouteContent => {
  const frContent: TransferRouteContent = {
    lead: route.lead,
    highlights: route.highlights,
    faq: route.faq,
    seo: route.seo,
  };
  const editorial =
    locale === "en"
      ? (TRANSFER_ROUTES_EN[route.slug] ?? frContent)
      : locale === "ar"
        ? (TRANSFER_ROUTES_AR[route.slug] ?? frContent)
        : frContent;
  return withPricedContent(route, locale, editorial);
};

/**
 * Ajoute la FAQ chiffrée et, en FR/EN, le titre et la description qui portent
 * le prix (voir generatedContent.ts). Point unique : la vue, le JSON-LD
 * FAQPage et les métadonnées lisent tous `localizedRouteContent`, donc ce qui
 * est affiché et ce qui est déclaré à Google restent identiques.
 */
const withPricedContent = (
  route: TransferRoute,
  locale: Locale,
  editorial: TransferRouteContent,
): TransferRouteContent => {
  const { from, to } = getRoutePlaces(route);
  const names = {
    from: placeShortName(from, locale),
    to: placeShortName(to, locale),
  };
  const rows = getRoutePrices(route);
  const faq = buildTransferFaq(
    from,
    to,
    names,
    route.durationMin,
    rows,
    locale,
    editorial.faq,
  );
  const priced =
    locale === "ar"
      ? null
      : buildTransferSeo(from, to, names, route.durationMin, rows, locale);
  return {
    ...editorial,
    faq,
    seo: priced ? { ...editorial.seo, ...priced } : editorial.seo,
  };
};

/** Ordre d'affichage des classes dans les tableaux de prix (économique → premium). */
const VEHICLE_DISPLAY_ORDER: VehicleId[] = [
  "CLASS_E",
  "CLASS_V",
  "CLASS_S",
  "CLASS_R",
];

export type TransferPrice = {
  vehicleId: VehicleId;
  /** Libellé commercial ("Class E", "Class V"…). */
  classLabel: string;
  /** Nom usuel ("Class E"). */
  name: string;
  price: number;
  maxPassengers: number;
  maxLuggage: number;
};

/** Récupère un trajet par slug (ou undefined). */
export const getTransferRoute = (slug: string): TransferRoute | undefined =>
  TRANSFER_ROUTES.find((route) => route.slug === slug);

/** Liste des slugs (pour generateStaticParams + sitemap). */
export const transferRouteSlugs = (): string[] =>
  TRANSFER_ROUTES.map((route) => route.slug);

/** Les deux lieux d'un trajet, résolus. */
export const getRoutePlaces = (
  route: TransferRoute,
): { from: TransferPlace; to: TransferPlace } => ({
  from: getTransferPlace(route.fromId),
  to: getTransferPlace(route.toId),
});

/**
 * Prix réels du trajet par catégorie de véhicule, lus dans infos.ts.
 * Une classe sans prix défini pour ce trajet est silencieusement ignorée
 * (jamais d'affichage de prix fantôme).
 */
export const getRoutePrices = (route: TransferRoute): TransferPrice[] => {
  const key = getRouteKey(route.fromId, route.toId);
  const result: TransferPrice[] = [];
  for (const vehicleId of VEHICLE_DISPLAY_ORDER) {
    const vehicle = VEHICLES[vehicleId];
    const price = vehicle?.fixedTransfers?.[key];
    if (!vehicle || typeof price !== "number") continue;
    result.push({
      vehicleId,
      classLabel: vehicle.classLabel,
      name: vehicle.name,
      price,
      maxPassengers: vehicle.maxPassengers,
      maxLuggage: vehicle.maxLuggage,
    });
  }
  return result;
};

/** Prix minimum du trajet (pour "à partir de X €" et JSON-LD lowPrice). */
export const getRouteMinPrice = (route: TransferRoute): number | null => {
  const prices = getRoutePrices(route).map((p) => p.price);
  return prices.length > 0 ? Math.min(...prices) : null;
};

/** Libellé court "Cannes → Aéroport de Nice" (locale-aware, fallback FR). */
export const getRouteLabel = (
  route: TransferRoute,
  locale: Locale = "fr",
): string => {
  const { from, to } = getRoutePlaces(route);
  return `${placeShortName(from, locale)} → ${placeShortName(to, locale)}`;
};

/** Libellé bidirectionnel "Cannes ⇄ Aéroport de Nice" (locale-aware). */
export const getRouteLabelBidirectional = (
  route: TransferRoute,
  locale: Locale = "fr",
): string => {
  const { from, to } = getRoutePlaces(route);
  return `${placeShortName(from, locale)} ⇄ ${placeShortName(to, locale)}`;
};

/**
 * Trajets liés (maillage interne "colonies") : ceux qui partagent un lieu
 * avec le trajet courant, limités à `limit`. Jamais le trajet lui-même.
 */
export const getRelatedRoutes = (
  route: TransferRoute,
  limit = 3,
): TransferRoute[] => {
  const placeIds = new Set([route.fromId, route.toId]);
  const sharesPlace = (other: TransferRoute) =>
    placeIds.has(other.fromId) || placeIds.has(other.toId);
  const related = TRANSFER_ROUTES.filter(
    (other) => other.slug !== route.slug && sharesPlace(other),
  );
  const others = TRANSFER_ROUTES.filter(
    (other) => other.slug !== route.slug && !sharesPlace(other),
  );
  return [...related, ...others].slice(0, limit);
};
